
/*
 * Limora Quick View
 * Uses the theme's existing ModalDialog, modal-opener and product-form.
 * The only purchase action in Quick View is the normal Add to Cart form.
 * Dynamic Shopify checkout is removed from the popup, not the product page.
 */
if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.productElement = null;
      this._requestId = 0;
      this._controller = null;
      this._loadingOpener = null;
      this._paymentObserver = null;
    }

    hide(preventFocus = false) {
      this._requestId += 1;
      if (this._controller) this._controller.abort();
      this._controller = null;
      this.stopPaymentObserver();
      this.resetOpener();

      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification && this.openedBy && typeof cartNotification.setActiveElement === 'function') {
        cartNotification.setActiveElement(this.openedBy);
      }

      if (this.modalContent) this.modalContent.innerHTML = '';
      this.productElement = null;
      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    resetOpener() {
      const opener = this._loadingOpener;
      if (!opener) return;
      opener.removeAttribute('aria-disabled');
      opener.classList.remove('loading');
      const spinner = opener.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.add('hidden');
      this._loadingOpener = null;
    }

    async show(opener) {
      if (!opener || !this.modalContent || this._controller) return;
      const productUrl = opener.getAttribute('data-product-url');
      if (!productUrl) return;

      const requestId = ++this._requestId;
      const controller = new AbortController();
      this._controller = controller;
      this._loadingOpener = opener;

      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('loading');
      const spinner = opener.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      try {
        const response = await fetch(productUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('The product could not be loaded.');
        const responseText = await response.text();
        if (requestId !== this._requestId) return;

        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceProduct = responseHTML.querySelector('section[id^="MainProduct-"]');
        if (!sourceProduct) throw new Error('The product information is unavailable.');

        // Clone the product section so the original full product page is never modified.
        this.productElement = sourceProduct.cloneNode(true);
        this.removeDOMElements();
        this.preventDuplicatedIDs();

        // Preserve the section wrapper: the theme's ID-scoped CSS relies on it.
        await this.setInnerHTML(this.modalContent, this.productElement.outerHTML, requestId);
        if (requestId !== this._requestId) return;

        this.productElement = this.modalContent.querySelector('section[id^="MainProduct-"]');
        this.removeExtraPaymentButtons();
        this.startPaymentObserver();
        this.removeGalleryListSemantic();
        this.preventVariantURLSwitching();

        if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
          window.ProductModel.loadShopifyXR();
        }

        // Do not call Shopify.PaymentButton.init() here. The popup does not
        // contain a dynamic checkout button; the normal product page still does.
        super.show(opener);
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this._requestId) return;
        console.error('Limora Quick View:', error);
        this.stopPaymentObserver();
        this.modalContent.innerHTML = '';

        const message = document.createElement('p');
        message.setAttribute('role', 'alert');
        message.textContent = 'Unable to load Quick View. Please open the product page instead.';
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = 'View product';
        this.modalContent.append(message, link);
        super.show(opener);
      } finally {
        if (requestId === this._requestId) this._controller = null;
        if (this._loadingOpener === opener) this.resetOpener();
      }
    }

    // External assets already present on the collection page must not be
    // executed again: doing so can register product-form handlers twice.
    async loadExternalScript(src, type) {
      const url = new URL(src, document.baseURI).href;
      if (!/^https?:$/.test(new URL(url).protocol)) return;

      const cache = window.__limoraQuickViewAssetCache ||
        (window.__limoraQuickViewAssetCache = new Map());

      if (cache.has(url)) return cache.get(url);

      const existing = Array.from(document.scripts).find(script =>
        script.src === url && !this.modalContent.contains(script)
      );
      if (existing) return;

      const promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        if (type) script.type = type;
        script.async = false;
        script.onload = resolve;
        script.onerror = () => reject(new Error('A product asset could not be loaded.'));
        document.head.appendChild(script);
      });

      cache.set(url, promise);
      return promise;
    }

    async setInnerHTML(element, html, requestId) {
      element.innerHTML = html;
      const scripts = Array.from(element.querySelectorAll('script'));

      for (const script of scripts) {
        if (!script.src) continue;
        await this.loadExternalScript(script.src, script.type);
        if (requestId !== this._requestId) return;
        script.remove();
      }

      // JSON variant data must remain in place. Re-execute only the normal
      // inline scripts after their fetched DOM exists.
      scripts.forEach(oldScript => {
        if (!oldScript.isConnected || oldScript.src) return;
        const type = (oldScript.type || '').toLowerCase();

        if (type === 'application/json') return;
        if (type === 'application/ld+json') {
          oldScript.remove();
          return;
        }
        if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') return;
        if (requestId !== this._requestId) return;

        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attribute => {
          newScript.setAttribute(attribute.name, attribute.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
    }

    removeExtraPaymentButtons() {
      if (!this.modalContent) return;
      this.modalContent.querySelectorAll('.shopify-payment-button').forEach(element => element.remove());
    }

    startPaymentObserver() {
      this.stopPaymentObserver();
      if (!window.MutationObserver || !this.modalContent) return;
      this._paymentObserver = new MutationObserver(() => this.removeExtraPaymentButtons());
      this._paymentObserver.observe(this.modalContent, { childList: true, subtree: true });
    }

    stopPaymentObserver() {
      if (this._paymentObserver) this._paymentObserver.disconnect();
      this._paymentObserver = null;
    }

    preventVariantURLSwitching() {
      if (!this.modalContent) return;
      this.modalContent.querySelectorAll('variant-radios, variant-selects').forEach(picker => {
        picker.setAttribute('data-update-url', 'false');
      });
    }

    removeDOMElements() {
      if (!this.productElement) return;
      this.productElement.querySelectorAll(
        'pickup-availability, product-modal, .shopify-payment-button, script[type="application/ld+json"]'
      ).forEach(element => element.remove());
    }

    preventDuplicatedIDs() {
      if (!this.productElement) return;

      // The supplied product section has no data-section attribute.
      // Derive the real section ID from its MainProduct-* wrapper instead.
      const originalId = this.productElement.id.replace(/^MainProduct-/, '');
      const sectionId = this.productElement.dataset.section || originalId;
      if (!sectionId) return;

      const uniqueId = 'quickadd-' + sectionId + '-' + this.id.replace(/[^a-zA-Z0-9_-]/g, '-');

      // Keep all IDs, form attributes, CSS and variant script references aligned.
      this.productElement.innerHTML = this.productElement.innerHTML.split(sectionId).join(uniqueId);
      this.productElement.id = 'MainProduct-' + uniqueId;
      this.productElement.dataset.section = uniqueId;
      this.productElement.dataset.originalSection = sectionId;

      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach(picker => {
        picker.dataset.section = uniqueId;
        picker.dataset.originalSection = sectionId;
        picker.dataset.updateUrl = 'false';
      });
    }

    removeGalleryListSemantic() {
      if (!this.modalContent) return;

      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (galleryList) {
        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
      }

      this.modalContent.querySelectorAll(
        '[id^="dT_bundleSelector"], [class^="product__info-as-bottom-tabs"]'
      ).forEach(element => element.remove());
    }
  });
}